import { useRef, useCallback, useEffect, useMemo } from "react";

// Global context to persist across component re-renders and ensure singleton behavior
let globalAudioCtx: AudioContext | null = null;

type SoundType = "sine" | "square" | "sawtooth" | "triangle";

const useSounds = (isMuted: boolean) => {
  // Use a ref for isMuted so we don't have to recreate functions when it changes.
  // This provides a stable 'sounds' object to consumers.
  const isMutedRef = useRef(isMuted);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const initAudio = useCallback(() => {
    // 1. Create Context if missing
    if (!globalAudioCtx) {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        globalAudioCtx = new AudioContextClass();
      }
    }

    // 2. Resume if suspended (critical for Chrome/Safari autoplay policies)
    if (globalAudioCtx && globalAudioCtx.state === "suspended") {
      globalAudioCtx
        .resume()
        .catch((e) => console.warn("Audio resume failed:", e));
    }

    // 3. iOS/Mobile Unlock Hack: Play a silent buffer immediately
    // This tells the browser "the user interacted, allow audio now"
    if (globalAudioCtx && globalAudioCtx.state === "running") {
      try {
        const buffer = globalAudioCtx.createBuffer(1, 1, 22050);
        const source = globalAudioCtx.createBufferSource();
        source.buffer = buffer;
        source.connect(globalAudioCtx.destination);
        if (source.start) {
          source.start(0);
        } else if ((source as any).noteOn) {
          (source as any).noteOn(0);
        }
      } catch (e) {
        // Ignore errors if the buffer fails to play
      }
    }
  }, []);

  const playSound = useCallback(
    (type: SoundType, frequency: number, duration: number, volume = 1) => {
      if (isMutedRef.current) return;

      // Ensure we have a context.
      // Note: We rely on the App to call initAudio() on first user interaction
      // to avoid "AudioContext was not allowed to start" warnings here.
      if (!globalAudioCtx) return;

      // Double check state before playing
      if (globalAudioCtx.state === "suspended") {
        globalAudioCtx.resume().catch(() => {});
      }

      try {
        const oscillator = globalAudioCtx.createOscillator();
        const gainNode = globalAudioCtx.createGain();

        oscillator.type = type;
        oscillator.frequency.setValueAtTime(
          frequency,
          globalAudioCtx.currentTime
        );

        // Smooth envelope to prevent clicking
        gainNode.gain.setValueAtTime(0, globalAudioCtx.currentTime);
        gainNode.gain.linearRampToValueAtTime(
          volume,
          globalAudioCtx.currentTime + 0.01
        );
        gainNode.gain.exponentialRampToValueAtTime(
          0.001,
          globalAudioCtx.currentTime + duration
        );

        oscillator.connect(gainNode);
        gainNode.connect(globalAudioCtx.destination);

        oscillator.start(globalAudioCtx.currentTime);
        oscillator.stop(globalAudioCtx.currentTime + duration);
      } catch (error) {
        // Suppress errors
      }
    },
    []
  );

  const playClick = useCallback(
    (isLoud: boolean) => {
      const volume = isLoud ? 0.3 : 0.15;
      playSound("triangle", 1200, 0.05, volume);
    },
    [playSound]
  );

  const playCorrect = useCallback(() => {
    playSound("sine", 523.25, 0.1, 0.3); // C5
    setTimeout(() => playSound("sine", 659.25, 0.1, 0.3), 100); // E5
    setTimeout(() => playSound("sine", 783.99, 0.15, 0.3), 200); // G5
  }, [playSound]);

  const playIncorrect = useCallback(() => {
    playSound("sawtooth", 164.81, 0.3, 0.25); // E3
  }, [playSound]);

  // Memoize the return object to prevent unnecessary re-renders in consumers
  return useMemo(
    () => ({
      initAudio,
      playClick,
      playCorrect,
      playIncorrect,
    }),
    [initAudio, playClick, playCorrect, playIncorrect]
  );
};

export type Sounds = ReturnType<typeof useSounds>;

export default useSounds;
